import { combineEpics, ofType } from 'redux-observable'
import { EMPTY, forkJoin, of } from 'rxjs'
import { catchError, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { COMPANY_FILTER_CHANGE } from 'containers/PartnerOptions/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'

import { apiErrorMessage } from './apiErrorMessage'
import {
  ALL_TENANTS_KEY,
  CLONE_EVENT_RULE_ASYNC,
  CLOSE_DRAWER,
  CREATE_ENDPOINT_ASYNC,
  CREATE_EVENT_RULE_ASYNC,
  DELETE_ENDPOINT_ASYNC,
  DELETE_EVENT_RULE_ASYNC,
  DRAWER_MODES,
  DRAWER_TYPES,
  FETCH_ENDPOINT_DETAIL_ASYNC,
  FETCH_ENDPOINTS_ASYNC,
  FETCH_EVENT_RULE_DETAIL_ASYNC,
  FETCH_EVENT_RULES_ASYNC,
  FETCH_SCOPE_AUTHORITY_ASYNC,
  OPEN_DRAWER,
  SET_ACTIVE_TAB,
  SHOW_SEF_TOAST,
  STATUS,
  TABS,
  TOGGLE_ENDPOINT_STATUS_ASYNC,
  TOGGLE_EVENT_RULE_STATUS_ASYNC,
  UPDATE_ENDPOINT_ASYNC,
  UPDATE_EVENT_RULE_ASYNC,
} from './constants'
import {
  createdDestinationToEndpoint,
  destinationToEndpoint,
  endpointFormToActivatePayload,
  endpointFormToCreatePayload,
  endpointFormToUpdatePayload,
  endpointStatusTogglePayload,
} from './destinationPayload'
import { apiRuleToUi, uiRuleToWritePayload } from './eventRulePayload'
import { endpointsDraftedByRuleDelete, rulesDraftedByEndpointDelete } from './reducer'
import {
  selectChildTenants,
  selectEndpoints,
  selectEventRules,
  selectIsMspPartner,
} from './selectors'

const MOVED_TO_DRAFT_TOAST_MS = 8000

const companyId = (state$) => selectCompanyFilter(state$.value)?.value

const closeDrawer = () => ({ type: CLOSE_DRAWER })
const openRuleDrawerFor = (endpointId) => [
  { type: SET_ACTIVE_TAB, payload: { tab: TABS.EVENT_RULES } },
  {
    type: OPEN_DRAWER,
    payload: {
      drawerType: DRAWER_TYPES.EVENT_RULE,
      mode: DRAWER_MODES.CREATE,
      id: null,
      preselectEndpointIds: [endpointId],
    },
  },
]
const showSefToast = ({ text, message, detail, type = 'success', duration } = {}) => ({
  type: SHOW_SEF_TOAST,
  payload: {
    text: text || message,
    ...(detail != null ? { detail } : {}),
    type,
    ...(duration ? { duration } : {}),
  },
})

const fetchEndpointsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_ENDPOINTS_ASYNC.INITIATE),
    switchMap(() =>
      api.settings.listDestinations(companyId(state$), 'V2').pipe(
        map((list) =>
          FETCH_ENDPOINTS_ASYNC.onSuccess({ data: (list || []).map(destinationToEndpoint) })
        ),
        catchError((error) => of(epicsErrorHandler(FETCH_ENDPOINTS_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(FETCH_ENDPOINTS_ASYNC.CANCEL)))
      )
    )
  )

const refetchEndpointsOnCompanyChangeEpic = (action$) =>
  action$.pipe(
    ofType(COMPANY_FILTER_CHANGE),
    mergeMap(() =>
      of(
        FETCH_ENDPOINTS_ASYNC.initiate(),
        FETCH_EVENT_RULES_ASYNC.initiate(),
        FETCH_SCOPE_AUTHORITY_ASYNC.initiate()
      )
    )
  )

const openEndpointDrawerEpic = (action$) =>
  action$.pipe(
    ofType(OPEN_DRAWER),
    mergeMap((action) => {
      const { drawerType, mode, id } = action.payload
      if (drawerType !== DRAWER_TYPES.ENDPOINT || mode !== DRAWER_MODES.EDIT || id == null) {
        return EMPTY
      }
      return of(FETCH_ENDPOINT_DETAIL_ASYNC.initiate({ id }))
    })
  )

const fetchEndpointDetailEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_ENDPOINT_DETAIL_ASYNC.INITIATE),
    mergeMap((action) =>
      api.settings.getDestination(companyId(state$), action.payload.id).pipe(
        map((destination) =>
          FETCH_ENDPOINT_DETAIL_ASYNC.onSuccess({ data: destinationToEndpoint(destination) })
        ),
        catchError((error) => of(epicsErrorHandler(FETCH_ENDPOINT_DETAIL_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(FETCH_ENDPOINT_DETAIL_ASYNC.CANCEL)))
      )
    )
  )

const createEndpointEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_ENDPOINT_ASYNC.INITIATE),
    mergeMap((action) => {
      const { endpoint, meta } = action.payload

      const finishCreate = (created) => {
        const actions = [
          CREATE_ENDPOINT_ASYNC.onSuccess({ endpoint: created }),
          FETCH_ENDPOINTS_ASYNC.initiate(),
          FETCH_EVENT_RULES_ASYNC.initiate(),
          closeDrawer(),
        ]
        if (meta?.createRule) actions.push(...openRuleDrawerFor(created.id))
        if (meta?.toast) {
          actions.push(showSefToast(meta.toast))
        } else {
          actions.push(showSefToast({ message: 'Endpoint created' }))
        }
        return of(...actions)
      }

      return api.settings
        .createDestination(companyId(state$), endpointFormToCreatePayload(endpoint))
        .pipe(
          mergeMap((ajaxRes) => {
            const created = createdDestinationToEndpoint(ajaxRes.response, endpoint)
            if (!meta?.activate) {
              return finishCreate(created)
            }
            return api.settings
              .updateDestination(
                companyId(state$),
                created.id,
                endpointFormToActivatePayload(endpoint)
              )
              .pipe(
                mergeMap(() => finishCreate({ ...created, status: STATUS.ACTIVE })),
                catchError(() =>
                  of(
                    CREATE_ENDPOINT_ASYNC.onSuccess({ endpoint: created }),
                    FETCH_ENDPOINTS_ASYNC.initiate(),
                    closeDrawer(),
                    showSefToast({
                      message: 'Endpoint created as draft — activation failed',
                      type: 'error',
                    })
                  )
                )
              )
          }),
          catchError((error) =>
            of(
              epicsErrorHandler(CREATE_ENDPOINT_ASYNC.onError, error),
              showSefToast({
                message: apiErrorMessage(error, 'Failed to create endpoint'),
                type: 'error',
              })
            )
          ),
          takeUntil(action$.pipe(ofType(CREATE_ENDPOINT_ASYNC.CANCEL)))
        )
    })
  )

const updateEndpointEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_ENDPOINT_ASYNC.INITIATE),
    mergeMap((action) => {
      const { id, endpoint, meta } = action.payload
      return api.settings
        .updateDestination(companyId(state$), id, endpointFormToUpdatePayload(endpoint))
        .pipe(
          mergeMap(() =>
            of(
              UPDATE_ENDPOINT_ASYNC.onSuccess(),
              FETCH_ENDPOINTS_ASYNC.initiate(),
              FETCH_EVENT_RULES_ASYNC.initiate(),
              closeDrawer(),
              showSefToast(meta?.toast || { message: 'Endpoint updated' })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(UPDATE_ENDPOINT_ASYNC.onError, error),
              showSefToast({
                message: apiErrorMessage(error, 'Failed to update endpoint'),
                type: 'error',
              })
            )
          ),
          takeUntil(action$.pipe(ofType(UPDATE_ENDPOINT_ASYNC.CANCEL)))
        )
    })
  )

const deleteEndpointEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_ENDPOINT_ASYNC.INITIATE),
    mergeMap((action) =>
      api.settings.deleteDestination(companyId(state$), action.payload.id).pipe(
        mergeMap((ajaxRes) => {
          const draftedRuleIds = ajaxRes?.response?.draftedRuleIds
          const moved = Array.isArray(draftedRuleIds)
            ? draftedRuleIds.length
            : rulesDraftedByEndpointDelete(action.payload.id, selectEventRules(state$.value)).length
          return of(
            DELETE_ENDPOINT_ASYNC.onSuccess(),
            FETCH_ENDPOINTS_ASYNC.initiate(),
            FETCH_EVENT_RULES_ASYNC.initiate(),
            closeDrawer(),
            moved
              ? showSefToast({
                  message: 'Endpoint deleted',
                  detail: `${moved} ${moved === 1 ? 'rule' : 'rules'} moved to Draft`,
                  type: 'warning',
                  duration: MOVED_TO_DRAFT_TOAST_MS,
                })
              : showSefToast({ message: 'Endpoint deleted' })
          )
        }),
        catchError((error) =>
          of(
            epicsErrorHandler(DELETE_ENDPOINT_ASYNC.onError, error),
            showSefToast({ message: 'Failed to delete endpoint', type: 'error' })
          )
        ),
        takeUntil(action$.pipe(ofType(DELETE_ENDPOINT_ASYNC.CANCEL)))
      )
    )
  )

const endpointToggleToast = (currentStatus, targetStatus) => {
  if (targetStatus === STATUS.DRAFT) return 'Endpoint saved as draft'
  const turnsOff =
    targetStatus === STATUS.DISABLED ||
    (!targetStatus && (currentStatus === STATUS.ACTIVE || currentStatus === STATUS.ERROR))
  return turnsOff ? 'Endpoint deactivated' : 'Endpoint activated'
}

const toggleEndpointStatusEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(TOGGLE_ENDPOINT_STATUS_ASYNC.INITIATE),
    mergeMap((action) => {
      const endpoint = selectEndpoints(state$.value).find((item) => item.id === action.payload.id)
      if (!endpoint?.apiDestination) {
        return of(
          TOGGLE_ENDPOINT_STATUS_ASYNC.onError({ message: 'Endpoint not found' }),
          showSefToast({ message: 'Endpoint not found', type: 'error' })
        )
      }

      return api.settings
        .updateDestination(
          companyId(state$),
          action.payload.id,
          endpointStatusTogglePayload(
            endpoint.apiDestination,
            endpoint.status,
            action.payload.meta?.targetStatus
          )
        )
        .pipe(
          mergeMap(() =>
            of(
              TOGGLE_ENDPOINT_STATUS_ASYNC.onSuccess(),
              FETCH_ENDPOINTS_ASYNC.initiate(),
              showSefToast(
                action.payload.meta?.toast || {
                  message: endpointToggleToast(endpoint.status, action.payload.meta?.targetStatus),
                }
              )
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(TOGGLE_ENDPOINT_STATUS_ASYNC.onError, error),
              showSefToast({ message: 'Failed to update endpoint', type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(TOGGLE_ENDPOINT_STATUS_ASYNC.CANCEL)))
        )
    })
  )

const savedRuleToast = (action, fallback) =>
  showSefToast(action.payload.meta?.toast || { message: fallback })

const fetchEventRulesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_EVENT_RULES_ASYNC.INITIATE),
    switchMap(() =>
      api.settings.listEventRules(companyId(state$)).pipe(
        map((list) => FETCH_EVENT_RULES_ASYNC.onSuccess({ data: (list || []).map(apiRuleToUi) })),
        catchError((error) => of(epicsErrorHandler(FETCH_EVENT_RULES_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(FETCH_EVENT_RULES_ASYNC.CANCEL)))
      )
    )
  )

const activateDraftEndpoints = (api, company, endpoints, endpointIds = []) => {
  const drafts = endpoints.filter(
    (endpoint) => endpointIds.includes(endpoint.id) && endpoint.status === STATUS.DRAFT
  )
  if (!drafts.length) return of(null)
  return forkJoin(
    drafts.map((endpoint) =>
      api.settings.updateDestination(
        company,
        endpoint.id,
        endpointStatusTogglePayload(endpoint.apiDestination, endpoint.status)
      )
    )
  )
}

const sameCompanySelection = (current = [], stored = []) => {
  const normalize = (ids) => ids.map((id) => String(id)).sort()
  const left = normalize(current)
  const right = normalize(stored)
  return left.length === right.length && left.every((id, index) => id === right[index])
}

const writeRuleScope = (
  api,
  state$,
  company,
  ruleId,
  linkedCompanyIds = [],
  storedCompanyIds,
  justCreated = false
) => {
  if (!selectIsMspPartner(state$.value) || !linkedCompanyIds.length) return of(false)
  if (storedCompanyIds && sameCompanySelection(linkedCompanyIds, storedCompanyIds)) return of(false)
  if (linkedCompanyIds.includes(ALL_TENANTS_KEY)) {
    return api.settings.setEventRuleScopeMode(company, ruleId, true).pipe(map(() => true))
  }
  const replaceCompanies = () =>
    api.settings.setEventRuleScope(company, ruleId, 'replace', linkedCompanyIds)
  const selectedMode$ = justCreated
    ? of(null)
    : api.settings.setEventRuleScopeMode(company, ruleId, false)
  return selectedMode$.pipe(
    mergeMap(replaceCompanies),
    map(() => true)
  )
}

const ruleSavedActions = (asyncType, action, fallbackToast) =>
  of(
    asyncType.onSuccess(),
    FETCH_ENDPOINTS_ASYNC.initiate(),
    FETCH_EVENT_RULES_ASYNC.initiate(),
    closeDrawer(),
    savedRuleToast(action, fallbackToast)
  )

const ruleSavedEndpointInactiveActions = (asyncType, error) =>
  of(
    asyncType.onSuccess(),
    FETCH_ENDPOINTS_ASYNC.initiate(),
    FETCH_EVENT_RULES_ASYNC.initiate(),
    closeDrawer(),
    showSefToast({
      message: `Event rule saved, but the endpoint could not be activated. ${apiErrorMessage(error, 'Activate it from the endpoint row.')}`,
      type: 'error',
    })
  )

const DRAFT_ENABLE_MESSAGE = 'The event rule needs at least one endpoint and a company scope.'

const ruleSavedAsDraftActions = (asyncType, error, companies) =>
  of(
    asyncType.onSuccess(),
    FETCH_EVENT_RULES_ASYNC.initiate(),
    closeDrawer(),
    showSefToast({
      message: `Saved as draft. ${apiErrorMessage(error, DRAFT_ENABLE_MESSAGE, companies)}`,
      type: 'error',
    })
  )

const saveThenEnable = ({
  api,
  state$,
  company,
  endpoints,
  asyncType,
  action,
  ruleId,
  wantsActive,
  reenableAfterScope = false,
  save$,
  fallback,
}) =>
  save$.pipe(
    mergeMap((saved) => {
      const id = ruleId ?? saved?.response?.id
      return writeRuleScope(
        api,
        state$,
        company,
        id,
        action.payload.rule.linkedCompanyIds,
        action.payload.rule.originalLinkedCompanyIds,
        ruleId == null
      ).pipe(
        mergeMap((scopeWritten) => {
          const enables = wantsActive || (reenableAfterScope && scopeWritten)
          return enables ? api.settings.enableEventRule(company, id) : of(null)
        }),
        mergeMap(() =>
          (wantsActive
            ? activateDraftEndpoints(
                api,
                company,
                endpoints,
                action.payload.rule.activateEndpointIds
              )
            : of(null)
          ).pipe(
            mergeMap(() => ruleSavedActions(asyncType, action, fallback)),
            catchError((error) => ruleSavedEndpointInactiveActions(asyncType, error))
          )
        ),
        catchError((error) =>
          ruleSavedAsDraftActions(asyncType, error, selectChildTenants(state$.value))
        )
      )
    })
  )

const createEventRuleEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_EVENT_RULE_ASYNC.INITIATE),
    mergeMap((action) => {
      const { rule } = action.payload
      const company = companyId(state$)
      return saveThenEnable({
        api,
        state$,
        company,
        endpoints: selectEndpoints(state$.value),
        asyncType: CREATE_EVENT_RULE_ASYNC,
        action,
        wantsActive: rule.status === STATUS.ACTIVE,
        save$: api.settings.createEventRule(
          company,
          uiRuleToWritePayload({ ...rule, status: STATUS.DRAFT })
        ),
        fallback: 'Event rule created',
      }).pipe(
        catchError((error) =>
          of(
            epicsErrorHandler(CREATE_EVENT_RULE_ASYNC.onError, error),
            showSefToast({
              message: apiErrorMessage(error, 'Failed to save event rule'),
              type: 'error',
            })
          )
        ),
        takeUntil(action$.pipe(ofType(CREATE_EVENT_RULE_ASYNC.CANCEL)))
      )
    })
  )

const updateEventRuleEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_EVENT_RULE_ASYNC.INITIATE),
    mergeMap((action) => {
      const { id, rule } = action.payload
      const stored = selectEventRules(state$.value).find((item) => item.id === id)
      const wantsActive = rule.status === STATUS.ACTIVE
      const stayActive = wantsActive && stored?.status === STATUS.ACTIVE
      const company = companyId(state$)
      return saveThenEnable({
        api,
        state$,
        company,
        endpoints: selectEndpoints(state$.value),
        asyncType: UPDATE_EVENT_RULE_ASYNC,
        action,
        ruleId: id,
        wantsActive: wantsActive && !stayActive,
        reenableAfterScope: stayActive,
        save$: api.settings.updateEventRule(
          company,
          id,
          uiRuleToWritePayload({ ...rule, status: stayActive ? STATUS.ACTIVE : STATUS.DRAFT })
        ),
        fallback: 'Changes saved',
      }).pipe(
        catchError((error) =>
          of(
            epicsErrorHandler(UPDATE_EVENT_RULE_ASYNC.onError, error),
            showSefToast({
              message: apiErrorMessage(error, 'Failed to save event rule'),
              type: 'error',
            })
          )
        ),
        takeUntil(action$.pipe(ofType(UPDATE_EVENT_RULE_ASYNC.CANCEL)))
      )
    })
  )

const returnEndpointsToDraft = (api, company, endpoints) =>
  forkJoin(
    endpoints.map((endpoint) =>
      api.settings.updateDestination(
        company,
        endpoint.id,
        endpointStatusTogglePayload(endpoint.apiDestination, endpoint.status, STATUS.DRAFT)
      )
    )
  )

const deleteEventRuleEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_EVENT_RULE_ASYNC.INITIATE),
    mergeMap((action) => {
      const orphaned = endpointsDraftedByRuleDelete(
        action.payload.id,
        selectEndpoints(state$.value)
      )
      const company = companyId(state$)

      return api.settings.deleteEventRule(company, action.payload.id).pipe(
        mergeMap(() => {
          const done = (toast, ...extra) =>
            of(
              DELETE_EVENT_RULE_ASYNC.onSuccess(),
              FETCH_ENDPOINTS_ASYNC.initiate(),
              FETCH_EVENT_RULES_ASYNC.initiate(),
              showSefToast(toast),
              ...extra
            )
          if (!orphaned.length) return done({ text: 'Event rule deleted' })

          return returnEndpointsToDraft(api, company, orphaned).pipe(
            mergeMap(() =>
              done({
                text: 'Event rule deleted',
                detail: `"${orphaned.map((item) => item.name).join(' and ')}" returned to Draft.`,
              })
            ),
            catchError(() =>
              done({
                text: 'Event rule deleted, but its endpoint could not be returned to Draft',
                detail: 'Save it as a draft from the endpoint row.',
                type: 'error',
              })
            )
          )
        }),
        catchError((error) =>
          of(
            epicsErrorHandler(DELETE_EVENT_RULE_ASYNC.onError, error),
            showSefToast({ message: 'Failed to delete event rule', type: 'error' })
          )
        ),
        takeUntil(action$.pipe(ofType(DELETE_EVENT_RULE_ASYNC.CANCEL)))
      )
    })
  )

const toggleEventRuleStatusEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(TOGGLE_EVENT_RULE_STATUS_ASYNC.INITIATE),
    mergeMap((action) => {
      const rule = selectEventRules(state$.value).find((item) => item.id === action.payload.id)
      if (!rule) {
        return of(showSefToast({ message: 'Event rule not found', type: 'error' }))
      }
      const toDraft = action.payload.meta?.targetStatus === STATUS.DRAFT
      let request
      if (toDraft) {
        request = api.settings.updateEventRule(
          companyId(state$),
          rule.id,
          uiRuleToWritePayload({ ...rule, status: STATUS.DRAFT })
        )
      } else if (rule.status === STATUS.ACTIVE) {
        request = api.settings.disableEventRule(companyId(state$), rule.id)
      } else {
        request = api.settings.enableEventRule(companyId(state$), rule.id)
      }
      return request.pipe(
        mergeMap(() =>
          of(
            TOGGLE_EVENT_RULE_STATUS_ASYNC.onSuccess(),
            FETCH_EVENT_RULES_ASYNC.initiate(),
            savedRuleToast(action, 'Event rule updated')
          )
        ),
        catchError((error) =>
          of(
            epicsErrorHandler(TOGGLE_EVENT_RULE_STATUS_ASYNC.onError, error),
            showSefToast({
              message: apiErrorMessage(error, 'Failed to update event rule'),
              type: 'error',
            })
          )
        ),
        takeUntil(action$.pipe(ofType(TOGGLE_EVENT_RULE_STATUS_ASYNC.CANCEL)))
      )
    })
  )

const cloneEventRuleEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CLONE_EVENT_RULE_ASYNC.INITIATE),
    mergeMap((action) =>
      api.settings.cloneEventRule(companyId(state$), action.payload.id).pipe(
        mergeMap(() =>
          of(
            CLONE_EVENT_RULE_ASYNC.onSuccess(),
            FETCH_EVENT_RULES_ASYNC.initiate(),
            savedRuleToast(action, 'Event rule cloned')
          )
        ),
        catchError((error) =>
          of(
            epicsErrorHandler(CLONE_EVENT_RULE_ASYNC.onError, error),
            showSefToast({ message: 'Failed to clone event rule', type: 'error' })
          )
        ),
        takeUntil(action$.pipe(ofType(CLONE_EVENT_RULE_ASYNC.CANCEL)))
      )
    )
  )

const fetchScopeAuthorityEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_SCOPE_AUTHORITY_ASYNC.INITIATE),
    switchMap(() => {
      if (!selectIsMspPartner(state$.value)) {
        return of(FETCH_SCOPE_AUTHORITY_ASYNC.onSuccess({ data: false }))
      }
      return api.settings.getEventRuleScopeAuthority(companyId(state$)).pipe(
        map((response) =>
          FETCH_SCOPE_AUTHORITY_ASYNC.onSuccess({
            data: response?.canEnableAllCompanies === true,
          })
        ),
        catchError((error) => of(epicsErrorHandler(FETCH_SCOPE_AUTHORITY_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(FETCH_SCOPE_AUTHORITY_ASYNC.CANCEL)))
      )
    })
  )

const fetchEventRuleDetailEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(OPEN_DRAWER),
    switchMap((action) => {
      const { drawerType, mode, id } = action.payload
      if (drawerType !== DRAWER_TYPES.EVENT_RULE || mode !== DRAWER_MODES.EDIT || id == null) {
        return EMPTY
      }
      return api.settings.getEventRule(companyId(state$), id).pipe(
        map((rule) => FETCH_EVENT_RULE_DETAIL_ASYNC.onSuccess({ data: apiRuleToUi(rule) })),
        catchError((error) => of(epicsErrorHandler(FETCH_EVENT_RULE_DETAIL_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(CLOSE_DRAWER)))
      )
    })
  )

export default combineEpics(
  fetchEndpointsEpic,
  refetchEndpointsOnCompanyChangeEpic,
  openEndpointDrawerEpic,
  fetchEndpointDetailEpic,
  createEndpointEpic,
  updateEndpointEpic,
  deleteEndpointEpic,
  toggleEndpointStatusEpic,
  fetchEventRulesEpic,
  createEventRuleEpic,
  updateEventRuleEpic,
  deleteEventRuleEpic,
  toggleEventRuleStatusEpic,
  cloneEventRuleEpic,
  fetchScopeAuthorityEpic,
  fetchEventRuleDetailEpic
)
